/* Line-up einer Kapsel (Paket E3, BAUPLAN-ABLAGE.md, Abschnitt 6, und Paket T, BAUPLAN-TIMETABLE.md). Reine Logik ohne React, getestet
   in lineuplogik.test.ts. Die Gage steht nicht im Line-up, sondern als Budgetposten „Künstler“ (posten_id). RLS am Posten verbirgt den
   Betrag. Zeiten sind Uhrzeiten ohne Datum: Ist das Ende kleiner als der Beginn, endet der Act am Folgetag (ADR-019). */

import type { StatusOption } from "../../kern/ui/Statuswahl";

export type LineupStatus = "offen" | "angefragt" | "zugesagt" | "vertrag";

export interface Act {
  id: string;
  act: string;
  beschreibung: string;
  /** JJJJ-MM-TT oder null */
  tag: string | null;
  /** HH:MM:SS aus der Datenbank, null: Act ohne Slot (Paket T1) */
  beginn: string | null;
  ende: string | null;
  /** Freier Text aus Paket E, seit T1 durch floor_id abgelöst (ADR-019). Die Oberfläche zeigt ihn nicht mehr. */
  buehne: string;
  status: LineupStatus;
  posten_id: string | null;
  /** Floor aus derselben Veranstaltung, null: Ohne Slot (Paket T1) */
  floor_id: string | null;
}

/** Floor einer Veranstaltung (Paket T1): Spalte im Timetable */
export interface Floor {
  id: string;
  name: string;
  reihenfolge: number;
}

/** Programmzeit der Veranstaltung, „HH:MM:SS“ oder null */
export interface Programm {
  beginn: string | null;
  ende: string | null;
}

export const LINEUP_STATUS: readonly { status: LineupStatus; text: string }[] = [
  { status: "offen", text: "Offen" },
  { status: "angefragt", text: "Angefragt" },
  { status: "zugesagt", text: "Zugesagt" },
  { status: "vertrag", text: "Vertrag liegt vor" },
];

/** Optionen der Statuswahl (BAUPLAN-STATUSWAHL.md, Abschnitt 3): drei Balken zeigen den Weg von Angefragt bis Vertrag */
export const LINEUP_OPTIONEN: readonly StatusOption<LineupStatus>[] = [
  { wert: "offen", text: "Offen", erklaerung: "Slot steht, Act fehlt noch", ton: "leer", stufe: 0 },
  { wert: "angefragt", text: "Angefragt", erklaerung: "Anfrage ist raus, Antwort fehlt", ton: "warten", stufe: 1 },
  { wert: "zugesagt", text: "Zugesagt", erklaerung: "Act hat zugesagt, Vertrag fehlt", ton: "ok", stufe: 2 },
  { wert: "vertrag", text: "Vertrag liegt vor", erklaerung: "Unterschrieben, liegt in Unterlagen", ton: "stark", stufe: 3 },
];

export const istLineupStatus = (x: unknown): x is LineupStatus => LINEUP_STATUS.some((s) => s.status === x);
export const statusText = (s: LineupStatus) => LINEUP_STATUS.find((x) => x.status === s)?.text ?? s;
/** Zugesagt zählt alles ab „Zugesagt“, auch „Vertrag liegt vor“ */
export const istZugesagt = (s: LineupStatus) => s === "zugesagt" || s === "vertrag";

/** „14:30“ aus „14:30:00“ */
export const uhrzeit = (t: string | null) => (t ? t.slice(0, 5) : "");

/** Minuten seit 0:00 aus „HH:MM“ oder „HH:MM:SS“ */
export const minuten = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** Dauer in Minuten wie aufgaben_lineup_dauer in der Datenbank: Ende vor Beginn heißt Folgetag (22:00 bis 01:00 sind 180 Minuten).
 *  null ohne Beginn oder Ende. */
export function dauer(beginn: string | null, ende: string | null): number | null {
  if (!beginn || !ende) return null;
  return (((minuten(ende) - minuten(beginn)) % 1440) + 1440) % 1440;
}

/** Beginn aufsteigend, ohne Beginn zuletzt */
const nachBeginn = (a: string | null, b: string | null) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a.localeCompare(b));

/** Nach Tag (ohne Tag zuerst), dann Beginn (ohne Beginn zuletzt), dann Act */
export function sortiert(acts: readonly Act[]): Act[] {
  return [...acts].sort((a, b) => (a.tag ?? "").localeCompare(b.tag ?? "") || nachBeginn(a.beginn, b.beginn) || a.act.localeCompare(b.act, "de"));
}

const WOCHENTAG = ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."];

/** „Sa. 05.12.“ oder „Ohne Tag“ */
export function tagText(tag: string | null): string {
  if (!tag) return "Ohne Tag";
  const d = new Date(`${tag}T12:00:00`);
  return `${WOCHENTAG[d.getDay()]} ${tag.slice(8, 10)}.${tag.slice(5, 7)}.`;
}

/** Acts je Tag in Reihenfolge. Nur ein Tag: eine Gruppe, die Seite zeigt dann keine Überschrift. */
export function nachTagen(acts: readonly Act[]): { tag: string | null; acts: Act[] }[] {
  const gruppen: { tag: string | null; acts: Act[] }[] = [];
  for (const a of sortiert(acts)) {
    const letzte = gruppen.at(-1);
    if (letzte && letzte.tag === a.tag) letzte.acts.push(a);
    else gruppen.push({ tag: a.tag, acts: [a] });
  }
  return gruppen;
}

/** Kopfzeile: „3 von 5 Slots zugesagt“ */
export function zusagenText(acts: readonly Act[]): string {
  return `${acts.filter((a) => istZugesagt(a.status)).length} von ${acts.length} ${acts.length === 1 ? "Slot" : "Slots"} zugesagt`;
}

/** Summe der Gagen aus den verknüpften Posten. Ein Posten zählt einmal, auch wenn zwei Acts ihn teilen. null: keine Gage sichtbar. */
export function gagenSumme(acts: readonly Act[], gagen: ReadonlyMap<string, number>): number | null {
  const posten = new Set(acts.flatMap((a) => (a.posten_id && gagen.has(a.posten_id) ? [a.posten_id] : [])));
  if (!posten.size) return null;
  return [...posten].reduce((s, id) => s + (gagen.get(id) ?? 0), 0);
}

/** Betrag in Euro aus der Eingabe („250“, „250,50“, „1.200,00“). Leer: null. Ungültig oder negativ: Fehlertext. */
export function gageLesen(text: string): { ok: true; cent: number | null } | { ok: false; fehler: string } {
  const t = text.trim().replace(/\s|€/g, "");
  if (!t) return { ok: true, cent: null };
  if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$/.test(t))
    return { ok: false, fehler: "Bitte gib die Gage als Betrag ein, zum Beispiel 250 oder 250,50." };
  const cent = Math.round(Number(t.replace(/\./g, "").replace(",", ".")) * 100);
  if (cent > 2_000_000_000) return { ok: false, fehler: "Der Betrag ist zu groß." };
  return { ok: true, cent };
}

/** Prüft ein Formular für einen Act wie die Datenbank. Gibt Fehler je Feld zurück, leer heißt gültig. Ohne Beginn steht der Act unter
 *  „Ohne Slot“ (Paket T1). */
export function actPruefen(f: { act: string; beginn: string; ende: string }): Partial<Record<"act" | "beginn" | "ende", string>> {
  const fehler: Partial<Record<"act" | "beginn" | "ende", string>> = {};
  if (!f.act.trim()) fehler.act = "Bitte gib den Act ein.";
  else if (f.act.trim().length > 200) fehler.act = "Der Act hat höchstens 200 Zeichen.";
  if (!f.beginn && f.ende) fehler.beginn = "Ein Ende gibt es nur mit Beginn.";
  if (f.beginn && f.ende && uhrzeit(f.ende) === uhrzeit(f.beginn))
    fehler.ende = "Beginn und Ende sind gleich. Ende vor Beginn heißt: nach Mitternacht.";
  return fehler;
}

/** Wer das Line-up pflegt: Vorstand mit vorhaben.verwalten immer, die Leitung im aktiven Vorhaben (wie die Datenbank) */
export function darfLineupPflegen(verwalten: boolean, rolle: "leitend" | "mitwirkend" | null, aktiv: boolean): boolean {
  return verwalten || (rolle === "leitend" && aktiv);
}

/* ---------------------------------------------------------------------------------------------------------------------------------
   Timetable (Paket T2, BAUPLAN-TIMETABLE.md, Abschnitt 5, ADR-019). Zeiten liegen auf einer Zeitachse in Minuten ab 0:00 des Tags der
   Veranstaltung: Eine Uhrzeit vor dem Schnitt gehört in die Nacht danach (+1440). Der Schnitt liegt in der Mitte der längsten Zeit
   ohne Programm und ohne Act, so landen 01:00 bei einer Party nach Mitternacht und 10:00 bei einem Fest am Morgen.
   --------------------------------------------------------------------------------------------------------------------------------- */

/** Raster im Timetable: 15 Minuten */
export const RASTER = 15;
/** Länge eines Acts ohne Ende im Timetable und beim Ziehen aus „Ohne Slot“ */
export const STANDARD_DAUER = 60;
const TAG = 1440;

/** Steht der Act im Timetable? Ohne Floor oder ohne Beginn: „Ohne Slot“. */
export const imRaster = (a: Pick<Act, "floor_id" | "beginn">): boolean => !!a.floor_id && !!a.beginn;

/** Auf 15 Minuten runden */
export const rasten = (min: number) => Math.round(min / RASTER) * RASTER;

/** „HH:MM“ aus Minuten auf der Zeitachse, auch über Mitternacht (1500 heißt 01:00) */
export function uhrAus(min: number): string {
  const t = ((Math.round(min) % TAG) + TAG) % TAG;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

/** Schnitt in Minuten seit 0:00: Mitte der längsten Lücke ohne Programm und ohne Act. Ohne beides 10:00 (Raster 18:00 bis 02:00). */
export function schnitt(acts: readonly Pick<Act, "beginn" | "ende">[], programm: Programm): number {
  const belegt = new Array<boolean>(TAG).fill(false);
  const belegen = (beginn: string, ende: string | null) => {
    const b = minuten(beginn);
    const d = dauer(beginn, ende) ?? STANDARD_DAUER;
    for (let i = 0; i < d; i++) belegt[(b + i) % TAG] = true;
  };
  if (programm.beginn) belegen(programm.beginn, programm.ende);
  for (const a of acts) if (a.beginn) belegen(a.beginn, a.ende);
  if (!belegt.includes(true)) return 10 * 60;
  if (!belegt.includes(false)) return programm.beginn ? minuten(programm.beginn) : 0;
  // längster freier Lauf im Kreis: ab einer belegten Minute zählen, damit kein Lauf über 0:00 zerschnitten wird
  const start = belegt.indexOf(true);
  let best = { von: 0, laenge: 0 };
  let lauf = { von: 0, laenge: 0 };
  for (let k = 1; k <= TAG; k++) {
    const i = (start + k) % TAG;
    if (!belegt[i]) {
      if (lauf.laenge === 0) lauf = { von: i, laenge: 0 };
      lauf.laenge++;
      if (lauf.laenge > best.laenge) best = { ...lauf };
    } else lauf = { von: 0, laenge: 0 };
  }
  return (best.von + Math.floor(best.laenge / 2)) % TAG;
}

/** Minuten auf der Zeitachse: vor dem Schnitt heißt nach Mitternacht */
export function aufAchse(t: string, schnittMin: number): number {
  const m = minuten(t);
  return m >= schnittMin ? m : m + TAG;
}

/** Lage eines Acts auf der Zeitachse. Ohne Ende 60 Minuten. null: ohne Beginn. */
export function lage(a: Pick<Act, "beginn" | "ende">, schnittMin: number): { von: number; bis: number } | null {
  if (!a.beginn) return null;
  const von = aufAchse(a.beginn, schnittMin);
  return { von, bis: von + (dauer(a.beginn, a.ende) ?? STANDARD_DAUER) };
}

/** Sichtbare Spanne des Timetables. Mit Programmzeit die Programmzeit, sonst frühester Beginn bis spätestes Ende mit je einer Stunde
 *  Rand, auf volle Stunden. Ohne Acts 18:00 bis 02:00. Acts außerhalb der Programmzeit erweitern die Spanne, damit jeder Act sichtbar
 *  bleibt. */
export function spanne(acts: readonly Pick<Act, "beginn" | "ende">[], programm: Programm, schnittMin: number): { von: number; bis: number } {
  const lagen = acts.flatMap((a) => lage(a, schnittMin) ?? []);
  const frueh = lagen.length ? Math.min(...lagen.map((x) => x.von)) : null;
  const spaet = lagen.length ? Math.max(...lagen.map((x) => x.bis)) : null;
  const stunde = (m: number, auf: boolean) => (auf ? Math.ceil(m / 60) : Math.floor(m / 60)) * 60;
  if (programm.beginn) {
    const von = aufAchse(programm.beginn, schnittMin);
    const bis = von + (dauer(programm.beginn, programm.ende) ?? (spaet !== null && spaet > von ? stunde(spaet + 60, true) - von : 8 * 60));
    return { von: frueh !== null ? Math.min(von, stunde(frueh, false)) : von, bis: spaet !== null ? Math.max(bis, stunde(spaet, true)) : bis };
  }
  if (frueh === null || spaet === null) return { von: 18 * 60, bis: 26 * 60 };
  return { von: stunde(frueh - 60, false), bis: stunde(spaet + 60, true) };
}

/** Acts, die sich auf einem Floor zeitlich überschneiden (am selben Tag), auch über Mitternacht. Nur Acts im Timetable. */
export function ueberschneidungen(acts: readonly Act[], schnittMin: number): Set<string> {
  const ids = new Set<string>();
  const im = acts.filter(imRaster);
  im.forEach((a, i) =>
    im.slice(i + 1).forEach((b) => {
      if (a.floor_id !== b.floor_id || a.tag !== b.tag) return;
      const la = lage(a, schnittMin);
      const lb = lage(b, schnittMin);
      if (la && lb && la.von < lb.bis && lb.von < la.bis) {
        ids.add(a.id);
        ids.add(b.id);
      }
    }),
  );
  return ids;
}

/** „22:00 bis 23:30“, „22:00“ oder „ohne Zeit“ */
export function zeitText(a: Pick<Act, "beginn" | "ende">): string {
  if (!a.beginn) return "ohne Zeit";
  return a.ende ? `${uhrzeit(a.beginn)} bis ${uhrzeit(a.ende)}` : uhrzeit(a.beginn);
}

/** „21:00 bis 05:00 (nächster Tag)“, „ab 21:00“ oder leer */
export function programmText(p: Programm): string {
  if (!p.beginn) return "";
  if (!p.ende) return `ab ${uhrzeit(p.beginn)}`;
  return `${uhrzeit(p.beginn)} bis ${uhrzeit(p.ende)}${minuten(p.ende) < minuten(p.beginn) ? " (nächster Tag)" : ""}`;
}

/** Vorgabe für den Umschalter: Timetable ab zwei Floors, sonst Liste */
export const vorgabeAnsicht = (floors: number): "liste" | "timetable" => (floors >= 2 ? "timetable" : "liste");

/** Neue Lage beim Verschieben um Minuten, im Raster und in der Spanne gehalten. Die Länge bleibt, ein Act ohne Ende bleibt ohne Ende. */
export function verschoben(
  a: Pick<Act, "beginn" | "ende">,
  um: number,
  schnittMin: number,
  sp: { von: number; bis: number },
): { beginn: string; ende: string | null } | null {
  const l = lage(a, schnittMin);
  if (!l) return null;
  const laenge = l.bis - l.von;
  const von = Math.max(sp.von, Math.min(sp.bis - laenge, rasten(l.von + um)));
  return { beginn: uhrAus(von), ende: a.ende ? uhrAus(von + laenge) : null };
}
